import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { ReactComponent as BagFood } from "../../../assets/svg/icons/bagFood.svg";
import { FilterDateDashboard } from "../../../components/Dashboard/ViewDatas/FilterDate";
import { useFilter } from "../../../hooks/useFilter";
import {
    flattenSelection,
    selectionToNumericIds,
    selectionToSortedIdsKey,
    useFilterContextV2,
} from "../../../contexts/FilterContextV2";
import Constants from "../../../constants/Index";
import { DateModal } from "../../../components/Modal/DateFilter";
import { useForm, useWatch } from "react-hook-form";
import { IOptionProps } from "../../../@types/reactSelect/IOption";
import { analyticsService } from "../../../services/analyticsService";
import { exportDailySnapshotCarbonToExcel } from "../../../utils/excel/exportDailySnapshot";
import { format } from "date-fns";

export default function ExportCarbon() {
    const {
        setDashboard,
        setSideBarOptionSelected,
        onBoardingDate,
        rangeDays,
        setRangeDays,
        setHideBatchFilter,
        setHideMicroAreaFilter,
        setSelectRangeDate,
        setSnapshot,
        snapshot
    } = useFilter();

    const { selectedFarms, selectedAreas } = useFilterContextV2();

    const farmsFetchKey = useMemo(
        () => selectionToSortedIdsKey(selectedFarms),
        [selectedFarms],
    );
    const areasFetchKey = useMemo(
        () => selectionToSortedIdsKey(selectedAreas),
        [selectedAreas],
    );

    const {
        control,
        watch,
        setValue
    } = useForm();

    const selectedFarmsWatch = useWatch({
        control,
        name: "farms"
    });

    const normalizeAreasForDropdown = useCallback((areasList: IOptionProps[]): IOptionProps[] => {
        return areasList.map((area: any) => ({
            ...area,
            value: String(area.value),
        }));
    }, []);

    useEffect(() => {
        setHideBatchFilter(true);
        setHideMicroAreaFilter(true);
        setSideBarOptionSelected("DASHBOARD")
        setDashboard("CARBON")
        setSelectRangeDate(2)
        document.title = Constants.TITLE_CARBON;

        const now = new Date();
        const lastDayOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0);

        if (!rangeDays) {
            setRangeDays({
                from: lastDayOfPreviousMonth,
                to: now
            })
        }
    }, []);

    const [isLoading, setIsLoading] = useState(false);
    const previousFarmsRef = useRef<any[]>([]);

    // Monitorar mudanças nas fazendas selecionadas
    useEffect(() => {
        const selectedFarms = selectedFarmsWatch || [];
        const selectedAreas = watch("areas") || [];
        const selectedFarmIds = selectedFarms.map((farm: any) => Number(farm.value));

        if (selectedAreas.length > 0 && selectedFarms.length > 0) {
            const validAreas = selectedAreas.filter((area: any) =>
                selectedFarmIds.includes(area.info.farm.id)
            );

            // Se algum retiro foi removido, atualizar o campo, normalizando valores
            if (validAreas.length !== selectedAreas.length) {
                const normalizedValidAreas = normalizeAreasForDropdown(validAreas);
                setValue("areas", normalizedValidAreas);
                previousFarmsRef.current = selectedFarms;
                return;
            }
        } else if (selectedFarms.length === 0 && selectedAreas.length > 0) {
            // Se não há fazendas selecionadas, remover todas as áreas
            setValue("areas", []);
            previousFarmsRef.current = selectedFarms;
            return;
        }

        previousFarmsRef.current = selectedFarms;
    }, [selectedFarmsWatch, setValue, watch, normalizeAreasForDropdown]);

    const handleExport = async (data: any) => {
        try {
            if (!rangeDays?.from || !rangeDays?.to) {
                alert("Por favor, selecione um período de datas");
                return;
            }

            const startDate = format(new Date(rangeDays.from), 'yyyy-MM-dd');
            const endDate = format(new Date(rangeDays.to), 'yyyy-MM-dd');
            const filename = `daily-snapshot-by-carbono-${startDate}-to-${endDate}.xlsx`

            exportDailySnapshotCarbonToExcel(data, filename);
        } catch (error) {
            console.error('Erro ao exportar dados:', error);
            alert("Erro ao gerar arquivo Excel. Tente novamente.");
        } finally {
            setIsLoading(false);
        }
    };

    const [mapByBatches, setMapByBatches] = useState(new Map());
    const [mapByMicroArea, setMapByMicroArea] = useState(new Map());

    useEffect(() => {
        const controller = new AbortController();

        if (rangeDays && rangeDays.from && rangeDays.to) {
            const farmsIds = selectionToNumericIds(selectedFarms);
            const areasIds = flattenSelection(selectedAreas)
                .filter((area) =>
                    farmsIds.includes(
                        Number(
                            (area.info as { farm?: { id: number } } | undefined)?.farm
                                ?.id,
                        ),
                    ),
                )
                .map((a) => Number(a.value));
            const startDate = format(new Date(rangeDays.from), 'yyyy-MM-dd');
            const endDate = format(new Date(rangeDays.to), 'yyyy-MM-dd');
            analyticsService.getDailySnapshot(farmsIds, areasIds, startDate, endDate, false, controller.signal)
                .then((data) => {
                    setSnapshot(data)

                    const byBatchMap = new Map<string, any[]>();
                    const byMicroAreaMap = new Map<string, any[]>();

                    (data || []).forEach((item: any) => {
                        const batchKey = item.batch_initials;
                        const microAreaKey = item.micro_area_initials;

                        const batchEntry = {
                            date: item.snapshot_date,
                            initials: `${item.farm_initials}-${item.area_initials}-${item.batch_initials}`,
                            days: item.supplement_offer_days,
                            total_category: item.total_from_category,
                            total_methane_emission_kg_day: item.total_methane_emission_kg_day,
                            co2_equivalent_kg: item.co2_equivalent_kg,
                        };

                        const microAreaEntry = {
                            date: item.snapshot_date,
                            initials: `${item.farm_initials}-${item.area_initials}-${item.micro_area_initials}`,
                            days: item.supplement_offer_days,
                            acreage: item.micro_area_acreage,
                            area_ms_intake_kg_ha: item.area_ms_intake_kg_ha,
                            total_methane_emission_kg_day: item.total_methane_emission_kg_day,
                            co2_equivalent_kg: item.co2_equivalent_kg,
                        };

                        if (batchKey) {
                            const batchArr = byBatchMap.get(batchKey);
                            if (batchArr) batchArr.push(batchEntry);
                            else byBatchMap.set(batchKey, [batchEntry]);
                        }

                        if (microAreaKey) {
                            const microArr = byMicroAreaMap.get(microAreaKey);
                            if (microArr) microArr.push(microAreaEntry);
                            else byMicroAreaMap.set(microAreaKey, [microAreaEntry]);
                        }
                    });

                    setMapByBatches(byBatchMap);
                    setMapByMicroArea(byMicroAreaMap);
                })
                .catch((err) => {
                    if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
                        // requisição abortada, ignorar
                        return;
                    }
                    // outros erros podem ser tratados aqui
                    console.error(err);
                });
        }

        return () => {
            controller.abort();
        };

    }, [farmsFetchKey, areasFetchKey, rangeDays]);

    return (
        <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
            <DateModal />

            <form className="dashboardPastureManagement">
                <div className="cattleMap_page">
                    <div className="cattleMap_header">
                        <BagFood height={72} />
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                        }}>
                            <h2 className="cattleMap_title">
                                Relatório
                            </h2>
                            <h3 className="cattleMap_subtitle">
                                Dados de Carbono
                            </h3>
                        </div>
                    </div>

                    <div>
                        <FilterDateDashboard exportPDF={true} onExportExcel={() => handleExport(snapshot)} />
                    </div>



                    <div className="splitContent" style={{
                        flexDirection: "column",
                        gap: 50,
                        display: (snapshot && snapshot.length > 0) ? "flex" : "none"
                    }}>
                        <h2 style={{
                            fontFamily: "Fira Sans",
                            fontStyle: `normal`,
                            fontWeight: `600`,
                            fontSize: `22px`,
                            lineHeight: `1.375rem`,
                            textTransform: `uppercase`,
                        }}>Números consolidados no período</h2>
                        <div className="tableDashboard">
                            <div style={{ overflowX: "auto", width: "100%" }}>
                                <h2>Consolidados por Lote</h2>
                                {(() => {
                                    const batches = Array.from(mapByBatches.values());
                                    const microAreas = Array.from(mapByMicroArea.values());

                                    const fmt = (value: number) =>
                                        Number.isFinite(value) ? value.toLocaleString("pt-BR", { maximumFractionDigits: 2 }) : "0";

                                    return (
                                        <>
                                            <table style={{ minWidth: "max-content", width: "100%" }}>
                                                <thead style={{ color: "#fff" }}>
                                                    <tr>
                                                        <th style={{ background: "#324440", minWidth: "80px", textAlign: "left" }}>Lote</th>
                                                        <th style={{ background: "#808080", minWidth: "80px" }}>Dias</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>Número de animais</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>Emissão total <br /> (kg CH4)</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>Emissão por animal <br /> (kg CH4/cab)</th>
                                                        <th style={{ background: "#324440", minWidth: "180px" }}>Emissão total <br /> (kg CO2 equivalente)</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>Emissão por animal <br /> (kg CO2 eq/cab)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {batches
                                                        .sort((a, b) => {
                                                            const initialsA = a[0]?.initials ?? "";
                                                            const initialsB = b[0]?.initials ?? "";
                                                            return initialsA.localeCompare(initialsB);
                                                        })
                                                        .map((row: any[], index: number) => {
                                                            if (!row || row.length === 0) return null;
                                                            const totalMethane = Number(row[row.length - 1]?.total_methane_emission_kg_day ?? 0);
                                                            const totalCo2 = Number(row[row.length - 1]?.co2_equivalent_kg ?? 0);
                                                            const animals = Number(row[row.length - 1]?.total_category ?? 0);
                                                            const methanePerAnimal = animals > 0 ? totalMethane / animals : 0;
                                                            const co2PerAnimal = animals > 0 ? totalCo2 / animals : 0;

                                                            return (
                                                                <tr className={index % 2 === 0 ? "bg_white" : "bg_gray"} key={`consolidated-${index}`}>
                                                                    <td>{row[row.length - 1]?.initials ?? "—"}</td>
                                                                    <td>{row[row.length - 1]?.days ?? "0"}</td>
                                                                    <td>{animals || "0"}</td>
                                                                    <td>{fmt(totalMethane)}</td>
                                                                    <td>{fmt(methanePerAnimal)}</td>
                                                                    <td>{fmt(totalCo2)}</td>
                                                                    <td>{fmt(co2PerAnimal)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                </tbody>
                                            </table>

                                            <br />

                                            <h2>Consolidados por Pasto</h2>
                                            <table style={{ minWidth: "max-content", width: "100%" }}>
                                                <thead style={{ color: "#fff" }}>
                                                    <tr>
                                                        <th style={{ background: "#324440", minWidth: "80px", textAlign: "left" }}>Pasto</th>
                                                        <th style={{ background: "#808080", minWidth: "80px" }}>Dias</th>
                                                        <th style={{ background: "#324440", minWidth: "80px" }}>Área</th>
                                                        <th style={{ background: "#324440", minWidth: "160px" }}>Forragem consumida <br /> (kg MS/ha)</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>Emissão total <br /> (kg CH4)</th>
                                                        <th style={{ background: "#324440", minWidth: "140px" }}>Emissão por área <br /> (kg CH4/ha)</th>
                                                        <th style={{ background: "#324440", minWidth: "160px" }}>kg CO2 equivalente</th>
                                                        <th style={{ background: "#324440", minWidth: "160px" }}>Emissão por área <br /> (kg CO2 eq/ha)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {microAreas
                                                        .sort((a, b) => {
                                                            const initialsA = a[0]?.initials ?? "";
                                                            const initialsB = b[0]?.initials ?? "";
                                                            return initialsA.localeCompare(initialsB);
                                                        })
                                                        .map((row: any[], index: number) => {
                                                            if (!row || row.length === 0) return null;
                                                            const totalForage = Number(row[row.length - 1]?.area_ms_intake_kg_ha ?? 0);
                                                            const totalMethane = Number(row[row.length - 1]?.total_methane_emission_kg_day ?? 0);
                                                            const totalCo2 = Number(row[row.length - 1]?.co2_equivalent_kg ?? 0);
                                                            const acreage = Number(row[row.length - 1]?.acreage ?? 0);
                                                            const methanePerAcre = acreage > 0 ? totalMethane / acreage : 0;
                                                            const co2PerAcre = acreage > 0 ? totalCo2 / acreage : 0;

                                                            return (
                                                                <tr className={index % 2 === 0 ? "bg_white" : "bg_gray"} key={`pasto-${index}`}>
                                                                    <td>{row[row.length - 1]?.initials ?? "—"}</td>
                                                                    <td>{row[row.length - 1]?.days ?? "0"}</td>
                                                                    <td>{acreage || "0"}</td>
                                                                    <td>{fmt(totalForage)}</td>
                                                                    <td>{fmt(totalMethane)}</td>
                                                                    <td>{fmt(methanePerAcre)}</td>
                                                                    <td>{fmt(totalCo2)}</td>
                                                                    <td>{fmt(co2PerAcre)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                </tbody>
                                            </table>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
}